import { useState, type RefObject, type ChangeEvent, type DragEvent } from "react";
import { clsx } from "clsx";
import * as XLSX from "xlsx";
import { IconPhoto, IconInfoCircle } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { UploadedFileCard } from "./uploaded-file-card";
import { BulkUploadGuideDialog } from "./bulk-upload-guide-dialog";
import { productRequestsService } from "../../../api_client";

interface Props {
  inputRef: RefObject<HTMLInputElement | null>;
  selectedFiles: File[];
  isDragging: boolean;
  isUploading: boolean;
  expectedHeaders: string[];
  onFileRemove: (index: number) => void;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => void;
}

function buildSampleRow(headers: string[], values: Record<string, string>): string[] {
  return headers.map((h) => values[h] ?? "");
}

export function BulkUploadView({
  inputRef, selectedFiles, isDragging, isUploading, expectedHeaders,
  onFileRemove, onDragOver, onDragLeave, onDrop, onFileChange, onUpload,
}: Props) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  async function handleDownloadTemplate() {
    setIsDownloading(true);
    try {
      const res = await productRequestsService.downloadImportTemplate({ responseType: "blob" });
      const blob = new Blob([res.data as unknown as BlobPart], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "product_upload_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      const ws = XLSX.utils.aoa_to_sheet([expectedHeaders]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Products");
      XLSX.writeFile(wb, "product_upload_template.xlsx");
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <>
    <div className="rounded-2xl border border-blue-100 bg-blue-50/40 px-5 py-4 flex items-start gap-3">
      <IconInfoCircle size={20} className="mt-0.5 shrink-0 text-blue-500" />
      <div className="flex flex-col gap-1">
        <Text variant="bodyBold" tone="dark">How bulk upload works</Text>
        <Text variant="bodySm" tone="body">
          Upload a spreadsheet with your products. Each row is one variant — rows with the same product name are grouped
          together automatically. All products are saved as drafts so you can review each one before submitting.
        </Text>
        <button type="button" className="mt-1 text-sm font-medium text-brandPrimary hover:underline self-start" onClick={() => setShowGuide(true)}>
          View full column guide
        </button>
      </div>
    </div>

    <div className="flex items-stretch gap-6 max-md:flex-col">
      <div className="flex flex-[2] flex-col">
        <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
          <Heading variant="h3" tone="dark" className="mb-5">Upload File with products</Heading>

          {selectedFiles.length > 0 ? (
            <div className="flex flex-col gap-3">
              {selectedFiles.map((file, index) => (
                <UploadedFileCard key={file.name} file={file} compact onRemove={() => onFileRemove(index)} />
              ))}
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  size="md"
                  className="!bg-textDark !border-textDark !px-8"
                  disabled={isUploading}
                  onClick={onUpload}
                >
                  {isUploading ? "Uploading…" : "Upload"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={clsx(
                  "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-7 outline-none transition-colors",
                  isDragging ? "border-brandPrimary bg-bgMuted" : "border-borderDefault bg-bgLight hover:border-borderDark",
                )}
              >
                <div className="flex size-12 items-center justify-center rounded-2xl bg-bgMuted">
                  <IconPhoto size={24} className="text-textMuted" />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Text variant="body" tone="body">Drag &amp; drop file here</Text>
                  <Text variant="bodySm" tone="muted">or click to browse</Text>
                </div>
              </div>
              <Text variant="bodySm" tone="dark" className="mt-3">Accepted: .xlsx, .csv</Text>
            </>
          )}

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={onFileChange}
          />
        </div>
      </div>

      <div className="w-72 flex-shrink-0 max-md:w-full">
        <div className="flex h-full flex-col rounded-2xl border border-borderDefault bg-bgWhite px-6 py-6 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
          <div className="flex flex-1 flex-col justify-center gap-5">
            <div className="flex flex-col gap-1">
              <Text variant="bodyBold" tone="dark">Need the template?</Text>
              <Text variant="bodySm" tone="muted">
                Use our pre-formatted spreadsheet so your data imports without errors.
              </Text>
            </div>
            <div className="border-t border-borderDefault" />
            <div className="flex flex-col gap-3">
              <Button
                variant="primary"
                size="md"
                fullWidth
                className="!bg-textDark !border-textDark"
                disabled={isDownloading}
                onClick={handleDownloadTemplate}
              >
                {isDownloading ? "Generating…" : "Download Template (.xlsx)"}
              </Button>
              <Text variant="bodySm" tone="muted">
                Tip: Keep column headers exactly as provided. Extra PricingTier columns are supported.
              </Text>
              <div className="border-t border-borderDefault" />
              <div className="flex flex-col gap-1">
                <Text variant="bodySmBold" tone="dark">See an example</Text>
                <Text variant="bodyXs" tone="muted">
                  Download a filled-in example to see how your data should look. Do not upload this file — create your own using the template above.
                </Text>
              </div>
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                onClick={() => {
                  const rows = [
                    buildSampleRow(expectedHeaders, {
                      Name: "MacBook Pro 16", Brand: "Apple", Category: "Ultrabooks",
                      Description: "High-performance laptop with M4 Pro chip, ideal for creative professionals and developers.",
                      ProductType: "consu",
                      Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                      CountryOfOrigin: "China", ScreenSize: "16 inch", Resolution: "Retina Display",
                      DealType: "Bulk Offer", DatasheetURL: "https://example.com/specs/macbook-pro-16.pdf",
                      MainImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                      GalleryImageURLs: "https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?w=800;https://images.unsplash.com/photo-1541807084-5c52b6b3adef?w=800",
                      Notes: "Allocate 15 units for Q3 corporate orders.",
                      Storage: "512 GB", Color: "Space Gray", SKU: "MBP16-512-SG-NEW",
                      ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                      Quantity: "15", Model: "MacBook Pro M4 Pro", Condition: "New",
                      CPU: "Apple M4", RAM: "16 GB",
                      VariantDatasheetURL: "https://example.com/specs/mbp16-512-sg.pdf",
                      VariantNotes: "Space Gray is the most popular color for corporate buyers.",
                      PricingTier_1_MinQty: "1", PricingTier_1_Price: "2499",
                      PricingTier_2_MinQty: "5", PricingTier_2_Price: "2399",
                      PricingTier_3_MinQty: "20", PricingTier_3_Price: "2299",
                    }),
                    buildSampleRow(expectedHeaders, {
                      Name: "MacBook Pro 16", Brand: "Apple", Category: "Ultrabooks",
                      Description: "High-performance laptop with M4 Pro chip, ideal for creative professionals and developers.",
                      ProductType: "consu",
                      Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                      CountryOfOrigin: "China", ScreenSize: "16 inch", Resolution: "Retina Display",
                      DealType: "Bulk Offer",
                      Storage: "1 TB", Color: "Silver", SKU: "MBP16-1TB-SLV-NEW",
                      ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                      Quantity: "10", Model: "MacBook Pro M4 Pro", Condition: "New",
                      CPU: "Apple M4", RAM: "32 GB",
                      PricingTier_1_MinQty: "1", PricingTier_1_Price: "2999",
                      PricingTier_2_MinQty: "5", PricingTier_2_Price: "2899",
                      PricingTier_3_MinQty: "20", PricingTier_3_Price: "2799",
                    }),
                    buildSampleRow(expectedHeaders, {
                      Name: "Dell Latitude 5550", Brand: "Dell", Category: "Business Laptops",
                      Description: "Reliable enterprise business laptop with Intel vPro for IT manageability.",
                      ProductType: "consu",
                      Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                      CountryOfOrigin: "China", ScreenSize: "15.6 inch", Resolution: "Full HD+ (1920x1200)",
                      DealType: "Bulk Offer", DatasheetURL: "https://example.com/specs/dell-lat-5550.pdf",
                      MainImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                      Notes: "Preferred model for government tenders.",
                      Storage: "256 GB", Color: "Black", SKU: "LAT5550-256-BLK-NEW",
                      ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                      Quantity: "50", Model: "Latitude 5550", Condition: "New",
                      CPU: "Intel Core i5", RAM: "8 GB",
                      PricingTier_1_MinQty: "1", PricingTier_1_Price: "1199",
                      PricingTier_2_MinQty: "10", PricingTier_2_Price: "1099",
                      PricingTier_3_MinQty: "25", PricingTier_3_Price: "999",
                    }),
                    buildSampleRow(expectedHeaders, {
                      Name: "Dell Latitude 5550", Brand: "Dell", Category: "Business Laptops",
                      Description: "Reliable enterprise business laptop with Intel vPro for IT manageability.",
                      ProductType: "consu",
                      Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                      CountryOfOrigin: "China", ScreenSize: "15.6 inch", Resolution: "Full HD+ (1920x1200)",
                      DealType: "Bulk Offer",
                      Storage: "512 GB", Color: "Black", SKU: "LAT5550-512-BLK-NEW",
                      ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                      Quantity: "30", Model: "Latitude 5550", Condition: "New",
                      CPU: "Intel Core i7", RAM: "16 GB",
                      VariantNotes: "i7 variant is standard for engineering teams.",
                      PricingTier_1_MinQty: "1", PricingTier_1_Price: "1499",
                      PricingTier_2_MinQty: "10", PricingTier_2_Price: "1399",
                    }),
                    buildSampleRow(expectedHeaders, {
                      Name: "HP EliteBook 840 G11", Brand: "HP", Category: "Business Laptops",
                      Description: "Compact 14-inch business laptop with Bang & Olufsen audio and HP Wolf Security.",
                      ProductType: "consu",
                      Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                      CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                      DealType: "Bulk Offer", DatasheetURL: "https://example.com/specs/hp-elitebook-840.pdf",
                      MainImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                      Storage: "256 GB", Color: "Silver", SKU: "HP840-256-SLV-NEW",
                      ImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                      Quantity: "40", Model: "EliteBook 840 G11", Condition: "New",
                      CPU: "Intel Core i7", RAM: "16 GB",
                      PricingTier_1_MinQty: "1", PricingTier_1_Price: "1299",
                      PricingTier_2_MinQty: "10", PricingTier_2_Price: "1199",
                      PricingTier_3_MinQty: "30", PricingTier_3_Price: "1099",
                    }),
                  ];
                  const instructionRows = [
                    ["THIS FILE IS AN EXAMPLE ONLY — DO NOT UPLOAD IT."],
                    ["Use it as a reference to understand the expected format, then create your own file using the blank template."],
                    [""],
                    ["Key rules:"],
                    ["• Rows with the same Name are grouped into one product with multiple variants."],
                    ["• Template-level fields (Brand, Category, Warranty, etc.) only need to appear on the first row of each product."],
                    ["• Each row must have at least one PricingTier (PricingTier_1_MinQty + PricingTier_1_Price)."],
                    ["• Values for Brand, Category, Storage, Color, etc. must match entries in the admin catalog exactly."],
                    ["• Image fields accept public URLs (https://...)."],
                    ["• GalleryImageURLs can contain multiple URLs separated by semicolons."],
                    [""],
                  ];
                  const wsInstructions = XLSX.utils.aoa_to_sheet(instructionRows);
                  wsInstructions["!cols"] = [{ wch: 120 }];
                  const wsData = XLSX.utils.aoa_to_sheet([expectedHeaders, ...rows]);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, wsInstructions, "README");
                  XLSX.utils.book_append_sheet(wb, wsData, "Example Products");
                  XLSX.writeFile(wb, "product_upload_example.xlsx");
                }}
              >
                Download Example (.xlsx)
              </Button>

              {import.meta.env.DEV && (
                <div className="flex flex-col gap-2">
                  <Text variant="capsBig" tone="muted">Dev test files</Text>
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => {
                      const rows = [
                        // MacBook Pro 16 — 3 variants
                        buildSampleRow(expectedHeaders, {
                          Name: "MacBook Pro 16", Brand: "Apple", Category: "Ultrabooks",
                          Description: "High-performance laptop", ProductType: "consu",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "16 inch", Resolution: "Retina Display",
                          DealType: "Bulk Offer", DatasheetURL: "https://example.com/mbp16.pdf",
                          MainImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          GalleryImageURLs: "https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?w=800",
                          Storage: "512 GB", Color: "Space Gray", SKU: "MBP16-512-SG",
                          ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          Quantity: "15", Model: "MacBook Pro M4 Pro", Condition: "New",
                          CPU: "Apple M4", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "2499",
                          PricingTier_2_MinQty: "5", PricingTier_2_Price: "2399",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "MacBook Pro 16", Brand: "Apple", Category: "Ultrabooks",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "16 inch", Resolution: "Retina Display",
                          Storage: "1 TB", Color: "Space Gray", SKU: "MBP16-1TB-SG",
                          ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          Quantity: "10", Model: "MacBook Pro M4 Pro", Condition: "New",
                          CPU: "Apple M4", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "2999",
                          PricingTier_2_MinQty: "5", PricingTier_2_Price: "2899",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "MacBook Pro 16", Brand: "Apple", Category: "Ultrabooks",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "16 inch", Resolution: "Retina Display",
                          Storage: "2 TB", Color: "Silver", SKU: "MBP16-2TB-SLV",
                          ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          Quantity: "5", Model: "MacBook Pro M4 Max", Condition: "New",
                          CPU: "Apple M4", RAM: "32 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "3799",
                          PricingTier_2_MinQty: "3", PricingTier_2_Price: "3599",
                          PricingTier_3_MinQty: "10", PricingTier_3_Price: "3399",
                        }),
                        // Dell Latitude 5550 — 2 variants
                        buildSampleRow(expectedHeaders, {
                          Name: "Dell Latitude 5550", Brand: "Dell", Category: "Business Laptops",
                          Description: "Enterprise business laptop", ProductType: "consu",
                          Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "15.6 inch", Resolution: "Full HD+ (1920x1200)",
                          DealType: "Bulk Offer", DatasheetURL: "https://example.com/lat5550.pdf",
                          MainImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Storage: "256 GB", Color: "Black", SKU: "LAT5550-256-BLK",
                          ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Quantity: "30", Model: "Latitude 5550", Condition: "New",
                          CPU: "Intel Core i5", RAM: "8 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1199",
                          PricingTier_2_MinQty: "10", PricingTier_2_Price: "1099",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "Dell Latitude 5550", Brand: "Dell", Category: "Business Laptops",
                          Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "15.6 inch", Resolution: "Full HD+ (1920x1200)",
                          Storage: "512 GB", Color: "Black", SKU: "LAT5550-512-BLK",
                          ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Quantity: "25", Model: "Latitude 5550", Condition: "New",
                          CPU: "Intel Core i7", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1499",
                          PricingTier_2_MinQty: "10", PricingTier_2_Price: "1399",
                        }),
                        // HP EliteBook 840 — 2 variants
                        buildSampleRow(expectedHeaders, {
                          Name: "HP EliteBook 840", Brand: "HP", Category: "Business Laptops",
                          Description: "Business ultrabook", ProductType: "consu",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          DealType: "Bulk Offer", DatasheetURL: "https://example.com/hp840.pdf",
                          MainImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                          Storage: "256 GB", Color: "Silver", SKU: "HP840-256-S",
                          ImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                          Quantity: "20", Model: "EliteBook 840 G11", Condition: "New",
                          CPU: "Intel Core i7", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1299",
                          PricingTier_2_MinQty: "10", PricingTier_2_Price: "1199",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "HP EliteBook 840", Brand: "HP", Category: "Business Laptops",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          Storage: "512 GB", Color: "Silver", SKU: "HP840-512-S",
                          ImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                          Quantity: "15", Model: "EliteBook 840 G11", Condition: "New",
                          CPU: "Intel Core i7", RAM: "32 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1599",
                          PricingTier_2_MinQty: "5", PricingTier_2_Price: "1499",
                        }),
                        // Lenovo ThinkPad X1 Carbon — 1 variant
                        buildSampleRow(expectedHeaders, {
                          Name: "Lenovo ThinkPad X1 Carbon", Brand: "Lenovo", Category: "Ultrabooks",
                          Description: "Premium ultralight business laptop", ProductType: "consu",
                          Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          DealType: "Bulk Offer",
                          MainImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Storage: "512 GB", Color: "Black", SKU: "X1C-512-BLK",
                          ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Quantity: "12", Model: "X1 Carbon Gen 12", Condition: "New",
                          CPU: "Intel Core i7", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1899",
                          PricingTier_2_MinQty: "5", PricingTier_2_Price: "1799",
                          PricingTier_3_MinQty: "15", PricingTier_3_Price: "1699",
                        }),
                      ];
                      const ws = XLSX.utils.aoa_to_sheet([expectedHeaders, ...rows]);
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, "Products");
                      XLSX.writeFile(wb, "sample_all_valid.xlsx");
                    }}
                  >
                    All Valid (4 products, 8 variants)
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => {
                      const rows = [
                        // Ghost brand — template-level error (bad brand + category)
                        buildSampleRow(expectedHeaders, {
                          Name: "Ghost Laptop X1", Brand: "FakeBrand999", Category: "Nonexistent Category",
                          Description: "Product with unknown brand & category", ProductType: "consu",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          DealType: "Bulk Offer",
                          MainImageURL: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=800",
                          ImageURL: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=800",
                          Storage: "256 GB", Color: "Silver", SKU: "GHOST-256-S",
                          Quantity: "5", Model: "X1", Condition: "New",
                          CPU: "Intel Core i5", RAM: "8 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "999",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "Ghost Laptop X1", Brand: "FakeBrand999", Category: "Nonexistent Category",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          ImageURL: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=800",
                          Storage: "512 GB", Color: "Black", SKU: "GHOST-512-B",
                          Quantity: "3", Model: "X1", Condition: "New",
                          CPU: "Intel Core i7", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1299",
                        }),
                        // Missing pricing tiers — parse-level error
                        buildSampleRow(expectedHeaders, {
                          Name: "Broken Tier Laptop", Brand: "Apple", Category: "Ultrabooks",
                          Description: "Variant with no pricing tiers", ProductType: "consu",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          DealType: "Bulk Offer",
                          MainImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          Storage: "512 GB", Color: "Space Gray", SKU: "NO-TIER-512",
                          Quantity: "10", Model: "No Tier", Condition: "New",
                          CPU: "Apple M4", RAM: "16 GB",
                        }),
                        // Bad variant refs — variant-level error
                        buildSampleRow(expectedHeaders, {
                          Name: "Bad Refs Laptop", Brand: "Apple", Category: "Ultrabooks",
                          Description: "Valid template but bad variant refs", ProductType: "consu",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          DealType: "Bulk Offer",
                          MainImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          Storage: "999 Petabytes", Color: "Invisible", SKU: "BADREF-001",
                          Quantity: "3", Model: "BadRef", Condition: "Alien Refurbished",
                          CPU: "Quantum X99", RAM: "1 Zettabyte",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "5000",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "Bad Refs Laptop", Brand: "Apple", Category: "Ultrabooks",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          Storage: "512 GB", Color: "Neon Pink", SKU: "BADREF-002",
                          Quantity: "2", Model: "BadRef v2", Condition: "Refurbished",
                          CPU: "Intel Core i5", RAM: "8 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "2500",
                        }),
                      ];
                      const ws = XLSX.utils.aoa_to_sheet([expectedHeaders, ...rows]);
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, "Products");
                      XLSX.writeFile(wb, "sample_all_errors.xlsx");
                    }}
                  >
                    All Errors (3 products, 5 variants)
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => {
                      const rows = [
                        // HP EliteBook 840 — 1 valid + 2 errors (bad color, no tier)
                        buildSampleRow(expectedHeaders, {
                          Name: "HP EliteBook 840", Brand: "HP", Category: "Business Laptops",
                          Description: "Business ultrabook", ProductType: "consu",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          DealType: "Bulk Offer", DatasheetURL: "https://example.com/hp840.pdf",
                          MainImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                          ImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                          Storage: "256 GB", Color: "Silver", SKU: "HP840-256-S",
                          Quantity: "20", Model: "EliteBook 840 G11", Condition: "New",
                          CPU: "Intel Core i7", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1299",
                          PricingTier_2_MinQty: "10", PricingTier_2_Price: "1199",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "HP EliteBook 840", Brand: "HP", Category: "Business Laptops",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          ImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                          Storage: "512 GB", Color: "Rainbow Sparkle", SKU: "HP840-512-BAD",
                          Quantity: "5", Model: "EliteBook 840 G11", Condition: "New",
                          CPU: "Intel Core i7", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1499",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "HP EliteBook 840", Brand: "HP", Category: "Business Laptops",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "Full HD (1920x1080)",
                          ImageURL: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800",
                          Storage: "1 TB", Color: "Silver", SKU: "HP840-1TB-NOTIER",
                          Quantity: "8", Model: "EliteBook 840 G11", Condition: "New",
                          CPU: "Intel Core i7", RAM: "16 GB",
                        }),
                        // Dell Latitude 5550 — 2 valid variants
                        buildSampleRow(expectedHeaders, {
                          Name: "Dell Latitude 5550", Brand: "Dell", Category: "Business Laptops",
                          Description: "Enterprise business laptop", ProductType: "consu",
                          Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "15.6 inch", Resolution: "Full HD+ (1920x1200)",
                          DealType: "Bulk Offer",
                          MainImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Storage: "256 GB", Color: "Black", SKU: "LAT5550-256-BLK",
                          ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Quantity: "30", Model: "Latitude 5550", Condition: "New",
                          CPU: "Intel Core i5", RAM: "8 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1199",
                          PricingTier_2_MinQty: "10", PricingTier_2_Price: "1099",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "Dell Latitude 5550", Brand: "Dell", Category: "Business Laptops",
                          Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "15.6 inch", Resolution: "Full HD+ (1920x1200)",
                          Storage: "512 GB", Color: "Black", SKU: "LAT5550-512-BLK",
                          ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Quantity: "25", Model: "Latitude 5550", Condition: "New",
                          CPU: "Intel Core i7", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1499",
                          PricingTier_2_MinQty: "10", PricingTier_2_Price: "1399",
                        }),
                        // MacBook Pro 16 — 2 valid variants
                        buildSampleRow(expectedHeaders, {
                          Name: "MacBook Pro 16", Brand: "Apple", Category: "Ultrabooks",
                          Description: "High-performance laptop", ProductType: "consu",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "16 inch", Resolution: "Retina Display",
                          DealType: "Bulk Offer",
                          MainImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          GalleryImageURLs: "https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?w=800",
                          Storage: "512 GB", Color: "Space Gray", SKU: "MBP16-512-SG",
                          ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          Quantity: "15", Model: "MacBook Pro M4 Pro", Condition: "New",
                          CPU: "Apple M4", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "2499",
                          PricingTier_2_MinQty: "5", PricingTier_2_Price: "2399",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "MacBook Pro 16", Brand: "Apple", Category: "Ultrabooks",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "16 inch", Resolution: "Retina Display",
                          Storage: "1 TB", Color: "Space Gray", SKU: "MBP16-1TB-SG",
                          ImageURL: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800",
                          Quantity: "10", Model: "MacBook Pro M4 Pro", Condition: "New",
                          CPU: "Apple M4", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "2999",
                          PricingTier_2_MinQty: "5", PricingTier_2_Price: "2899",
                        }),
                        // Lenovo ThinkPad T14s — all fail (bad resolution)
                        buildSampleRow(expectedHeaders, {
                          Name: "Lenovo ThinkPad T14s", Brand: "Lenovo", Category: "Business Laptops",
                          Description: "Compact business laptop", ProductType: "consu",
                          Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "WUXGA (1920x1200)",
                          DealType: "Bulk Offer",
                          MainImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Storage: "512 GB", Color: "Black", SKU: "T14S-512-BLK",
                          Quantity: "30", Model: "ThinkPad T14s Gen 5", Condition: "New",
                          CPU: "Intel Core i7", RAM: "32 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1599",
                          PricingTier_2_MinQty: "5", PricingTier_2_Price: "1499",
                        }),
                        buildSampleRow(expectedHeaders, {
                          Name: "Lenovo ThinkPad T14s", Brand: "Lenovo", Category: "Business Laptops",
                          Warranty: "3 years", WarrantyType: "Onsite", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "14 inch", Resolution: "WUXGA (1920x1200)",
                          ImageURL: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800",
                          Storage: "256 GB", Color: "Black", SKU: "T14S-256-BLK",
                          Quantity: "20", Model: "ThinkPad T14s Gen 5", Condition: "New",
                          CPU: "Intel Core i5", RAM: "16 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "1299",
                        }),
                        // Unknown Brand — template-level error (bad brand)
                        buildSampleRow(expectedHeaders, {
                          Name: "Unknown Brand Laptop", Brand: "XyzNotReal", Category: "Ultrabooks",
                          Description: "Whole product fails", ProductType: "consu",
                          Warranty: "1 year", WarrantyType: "Carry-in", WarrantyProvider: "Manufacturer warranty",
                          CountryOfOrigin: "China", ScreenSize: "15.6 inch", Resolution: "Full HD (1920x1080)",
                          DealType: "Bulk Offer",
                          MainImageURL: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=800",
                          ImageURL: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=800",
                          Storage: "256 GB", Color: "White", SKU: "UNK-256-W",
                          Quantity: "2", Model: "Unknown", Condition: "New",
                          CPU: "Intel Core i5", RAM: "8 GB",
                          PricingTier_1_MinQty: "1", PricingTier_1_Price: "799",
                        }),
                      ];
                      const ws = XLSX.utils.aoa_to_sheet([expectedHeaders, ...rows]);
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, "Products");
                      XLSX.writeFile(wb, "sample_mixed.xlsx");
                    }}
                  >
                    Mixed (5 products, 10 variants)
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => {
                      const productDefs = [
                        { name: "Dell Latitude 5450", brand: "Dell", cat: "Business Laptops", desc: "Intel Core i5-1345U · 14\" FHD anti-glare (1920×1080) · Windows 11 Pro · 58 Wh battery up to 12 hr · 1.43 kg · 2× USB-A 3.2, 1× USB-C DP, HDMI 2.0, RJ-45 · MIL-STD-810H tested", warranty: "3 years", wType: "Onsite", wProv: "Manufacturer warranty", country: "China", screen: "14 inch", res: "Full HD (1920x1080)", deal: "Bulk Offer", notes: "3-year Dell ProSupport Next Business Day. vPro ready. Fingerprint reader and IR camera for Windows Hello.", img: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=400", storages: ["256 GB", "512 GB", "1 TB"], colors: ["Black", "Silver"], rams: ["8 GB", "16 GB", "32 GB"], cpus: ["Intel Core i5", "Intel Core i7"], basePrice: 3900 },
                        { name: "HP EliteBook 840 G11", brand: "HP", cat: "Ultrabooks", desc: "Intel Core Ultra 5 125H · 14\" WUXGA IPS anti-glare · Windows 11 Pro · 56 Wh battery up to 14 hr · 1.36 kg · 2× Thunderbolt 4, 2× USB-A 3.2, HDMI 2.1", warranty: "3 years", wType: "Onsite", wProv: "Manufacturer warranty", country: "China", screen: "14 inch", res: "Full HD+ (1920x1200)", deal: "New Arrival", notes: "3-year HP Care Pack on-site. HP Wolf Security built-in. HP Sure Start BIOS protection. MIL-STD-810H.", img: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=400", storages: ["256 GB", "512 GB", "1 TB"], colors: ["Silver", "Black"], rams: ["16 GB", "32 GB"], cpus: ["Intel Core Ultra 5", "Intel Core Ultra 7"], basePrice: 4200 },
                        { name: "Lenovo ThinkPad X1 Carbon Gen 12", brand: "Lenovo", cat: "Ultrabooks", desc: "Intel Core Ultra 7 155H · 14\" 2.8K OLED 120Hz · Windows 11 Pro · 57 Wh battery up to 15 hr · 1.08 kg · 2× Thunderbolt 4, 2× USB-A 3.2, HDMI 2.1", warranty: "3 years", wType: "Onsite", wProv: "Manufacturer warranty", country: "China", screen: "14 inch", res: "Full HD+ (1920x1200)", deal: "New Arrival", notes: "3-year Lenovo Premier Support on-site. MIL-STD-810H. Intel vPro Enterprise.", img: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=400", storages: ["256 GB", "512 GB", "1 TB"], colors: ["Black", "Silver"], rams: ["16 GB", "32 GB"], cpus: ["Intel Core Ultra 7"], basePrice: 6500 },
                        { name: "Apple MacBook Pro 14 M3 Pro", brand: "Apple", cat: "Ultrabooks", desc: "Apple M3 Pro (11-core CPU, 14-core GPU) · 14.2\" Liquid Retina XDR (3024×1964) · macOS Sonoma · 70 Wh battery up to 17 hr · 1.61 kg · 3× Thunderbolt 4, HDMI 2.1, SD card, MagSafe 3", warranty: "1 year", wType: "Carry-in", wProv: "Manufacturer warranty", country: "China", screen: "14 inch", res: "Retina Display", deal: "Bulk Offer", notes: "AppleCare+ available. Supports up to three external displays.", img: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=400", storages: ["512 GB", "1 TB", "2 TB"], colors: ["Space Gray", "Silver"], rams: ["18 GB", "36 GB"], cpus: [], basePrice: 7800 },
                        { name: "Dell Precision 5680", brand: "Dell", cat: "Mobile Workstations", desc: "Intel Core i9-13900H · NVIDIA RTX 3500 Ada 12 GB · 16\" OLED 3.5K · Windows 11 Pro · 86 Wh battery · 1.91 kg · 2× Thunderbolt 4, 1× USB-A, SD card, HDMI 2.1", warranty: "3 years", wType: "Onsite", wProv: "Manufacturer warranty", country: "China", screen: "16 inch", res: "Full HD+ (1920x1200)", deal: "Bulk Offer", notes: "3-year Dell ProSupport Plus. ISV-certified for AutoCAD, SolidWorks, MATLAB.", img: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400", storages: ["512 GB", "1 TB", "2 TB"], colors: ["Graphite"], rams: ["32 GB", "64 GB"], cpus: ["Intel Core i7", "Intel Core i9"], basePrice: 9500 },
                        { name: "HP ProBook 450 G10", brand: "HP", cat: "Business Laptops", desc: "Intel Core i5-1335U · 15.6\" FHD IPS · Windows 11 Pro · 51 Wh battery up to 11 hr · 1.79 kg · 2× USB-A 3.2, 1× USB-C, HDMI 1.4, RJ-45", warranty: "1 year", wType: "Carry-in", wProv: "Manufacturer warranty", country: "China", screen: "15.6 inch", res: "Full HD (1920x1080)", deal: "Bulk Offer", notes: "1-year HP warranty. Budget-friendly for general office fleet deployment.", img: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=400", storages: ["256 GB", "512 GB"], colors: ["Silver", "Black"], rams: ["8 GB", "16 GB"], cpus: ["Intel Core i5"], basePrice: 3200 },
                        { name: "Acer TravelMate P4 14", brand: "Acer", cat: "Business Laptops", desc: "Intel Core i5-1340P · 14\" FHD IPS anti-glare · Windows 11 Pro · 56 Wh battery up to 13 hr · 1.4 kg · 2× USB-A, 1× USB-C Thunderbolt 4, HDMI 2.0, RJ-45", warranty: "3 years", wType: "Onsite", wProv: "Manufacturer warranty", country: "China", screen: "14 inch", res: "Full HD (1920x1080)", deal: "Bulk Offer", notes: "3-year Acer Commercial warranty. Antimicrobial Corning Gorilla Glass cover.", img: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=400", storages: ["256 GB", "512 GB", "1 TB"], colors: ["Black"], rams: ["16 GB", "32 GB"], cpus: ["Intel Core i5", "Intel Core i7"], basePrice: 3100 },
                        { name: "Samsung Galaxy Tab S9 FE", brand: "Samsung", cat: "Tablets", desc: "Exynos 1380 · 10.9\" TFT LCD (2304×1440) · Wi-Fi 6 · IP68 water/dust resistant · S Pen included · 8000 mAh battery · USB-C · 523 g", warranty: "1 year", wType: "Carry-in", wProv: "Manufacturer warranty", country: "China", screen: "10.9 inch", res: "Full HD (1920x1080)", deal: "Bulk Offer", notes: "1-year Samsung warranty. Knox enterprise security.", img: "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=400", storages: ["128 GB", "256 GB", "512 GB"], colors: ["Silver", "Graphite", "Blue"], rams: [], cpus: [], basePrice: 1500 },
                        { name: "Apple iPhone 15 Pro", brand: "Apple", cat: "Phones", desc: "A17 Pro chip · 6.1\" Super Retina XDR OLED (2556×1179) · 48MP main camera · USB-C · Face ID · Ceramic Shield · Titanium · 5G · IP68 · 187 g", warranty: "1 year", wType: "Carry-in", wProv: "Manufacturer warranty", country: "China", screen: "6.1 inch", res: "Full HD (1920x1080)", deal: "New Arrival", notes: "1-year Apple warranty. Action button. Apple Intelligence (iOS 18.1+).", img: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400", storages: ["128 GB", "256 GB", "512 GB", "1 TB"], colors: ["Black", "White", "Blue", "Gold"], rams: [], cpus: [], basePrice: 4200 },
                        { name: "Logitech MX Master 3S", brand: "Logitech", cat: "Mice", desc: "Wireless ergonomic mouse · 8000 DPI MagSpeed scroll · Bluetooth + Logi Bolt · Easy-Switch 3 devices · USB-C rechargeable · 70-day battery · 141 g", warranty: "2 years", wType: "Carry-in", wProv: "Manufacturer warranty", country: "China", screen: "", res: "", deal: "", notes: "2-year Logitech warranty. Works on any surface including glass.", img: "https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=400", storages: [], colors: ["Graphite", "White", "Black", "Silver", "Blue", "Gold", "Platinum", "Midnight"], rams: [], cpus: [], basePrice: 350 },
                      ];
                      const rows: string[][] = [];
                      const skuPrefix: Record<string, string> = {
                        "Dell Latitude 5450": "LAT5450", "HP EliteBook 840 G11": "EB840G11",
                        "Lenovo ThinkPad X1 Carbon Gen 12": "X1CG12", "Apple MacBook Pro 14 M3 Pro": "MBP14M3P",
                        "Dell Precision 5680": "PREC5680", "HP ProBook 450 G10": "PB450G10",
                        "Acer TravelMate P4 14": "TMP414", "Samsung Galaxy Tab S9 FE": "TABS9FE",
                        "Apple iPhone 15 Pro": "IP15PRO", "Logitech MX Master 3S": "MXM3S",
                      };
                      for (const p of productDefs) {
                        const storages = p.storages.length ? p.storages : [""];
                        const colors = p.colors.length ? p.colors : [""];
                        const rams = p.rams.length ? p.rams : [""];
                        const cpus = p.cpus.length ? p.cpus : [""];
                        const prefix = skuPrefix[p.name] ?? p.name.replace(/\s+/g, "").slice(0, 8).toUpperCase();
                        let varIdx = 0;
                        for (const storage of storages) {
                          for (const color of colors) {
                            for (const ram of rams) {
                              for (const cpu of cpus) {
                                varIdx++;
                                const storagePart = storage ? storage.replace(/\s/g, "") : "";
                                const colorPart = color ? color.replace(/\s/g, "").slice(0, 3).toUpperCase() : "";
                                const ramPart = ram ? ram.replace(/\s/g, "") : "";
                                const sku = `${prefix}-${storagePart}${colorPart ? `-${colorPart}` : ""}${ramPart ? `-${ramPart}` : ""}`;
                                const priceOffset = storages.indexOf(storage) * 200 + rams.indexOf(ram) * 150;
                                const price = p.basePrice + priceOffset;
                                const qty = Math.floor(Math.random() * 40) + 10;
                                rows.push(buildSampleRow(expectedHeaders, {
                                  Name: p.name, Brand: p.brand, Category: p.cat,
                                  Description: p.desc, ProductType: "consu",
                                  Warranty: p.warranty, WarrantyType: p.wType, WarrantyProvider: p.wProv,
                                  CountryOfOrigin: p.country,
                                  Notes: (p as any).notes ?? "",
                                  ...(p.screen ? { ScreenSize: p.screen } : {}),
                                  ...(p.res ? { Resolution: p.res } : {}),
                                  ...(p.deal ? { DealType: p.deal } : {}),
                                  MainImageURL: p.img,
                                  ImageURL: p.img,
                                  ...(storage ? { Storage: storage } : {}),
                                  ...(color ? { Color: color } : {}),
                                  SKU: sku,
                                  Quantity: String(qty),
                                  Model: p.name,
                                  Condition: "New",
                                  ...(ram ? { RAM: ram } : {}),
                                  ...(cpu ? { CPU: cpu } : {}),
                                  PricingTier_1_MinQty: "1", PricingTier_1_Price: String(price),
                                  PricingTier_2_MinQty: "5", PricingTier_2_Price: String(Math.round(price * 0.95)),
                                  PricingTier_3_MinQty: "25", PricingTier_3_Price: String(Math.round(price * 0.88)),
                                }));
                              }
                            }
                          }
                        }
                      }
                      const ws = XLSX.utils.aoa_to_sheet([expectedHeaders, ...rows]);
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, "Products");
                      XLSX.writeFile(wb, "sample_stress_test.xlsx");
                    }}
                  >
                    Full Demo (10 products, ~149 variants)
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <BulkUploadGuideDialog open={showGuide} onClose={() => setShowGuide(false)} />
    </>
  );
}
